import { useEffect, useMemo, useState } from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import Tab from "@mui/material/Tab";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import { colors } from "../../../theme";
import API from "../../../api";
import { startToast, updateToastError, updateToastSuccess } from "../../../utils/notification-loading";
import { mapAssetRows, vulnAckKey, assetIdentity, riskScoreStyle } from "../status/status-components/assetUtils";
import { resolveClassification } from "../status/status-components/assetClassification";
import { zoneRiskStyle, zoneRiskLabel } from "../status/status-components/zoneUtils";
import { computeZoneSecurityScore } from "../status/status-components/securityScore";
import AssetDetailContent from "../status/status-components/AssetDetailContent";
import IpHistoryDialog from "../status/status-components/IpHistoryDialog";
import EditAssetDialog from "../status/status-components/EditAssetDialog";
import ZoneManagerDialog from "../status/status-components/ZoneManagerDialog";
import TrafficConnectionsPanel from "./asset-detail-components/TrafficConnectionsPanel";
import TrafficLogsPanel from "./asset-detail-components/TrafficLogsPanel";
import NetworkIssuesPanel from "./asset-detail-components/NetworkIssuesPanel";
import MibPanel from "./asset-detail-components/MibPanel";

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const online = status === "online";
  return (
    <Chip
      label={status.toUpperCase()}
      size="small"
      sx={{
        bgcolor: online ? "#4cceac22" : "#ff444422",
        color:   online ? "#4cceac"   : "#ff6644",
        fontSize: 10, height: 20, fontWeight: 700, letterSpacing: 0.6,
      }}
    />
  );
};

// Full-page asset detail — reached directly from the Overview page's drill-down
// lists or an Inventory grid row/icon click (no intermediate drawer). Three tabs
// merge what used to be three separate "look up one host" experiences:
// Identity & Security (the former inventory drawer's content, reused via
// AssetDetailContent), Connections (from the removed Search by IP page — protocol
// breakdown and the internal connections table/graph), and Network Issues (the
// removed Network Analyses page's per-IP detail, plus the traffic timeline).
//
// Security, Connections and the Risk/OS/CVEs/Last-seen summary cards all depend
// on Orion being enabled for this deployment. Visibility is resolved once on
// mount via API.getOrionVisibility() and defaults to visible while that call is
// in flight, so nothing flashes and then disappears.
const AssetDetailPage = ({ multiSecurityView = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const assetId = new URLSearchParams(location.search).get("assetId");

  const [rows, setRows] = useState([]);
  const [zones, setZones] = useState([]);
  const [acks, setAcks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ipHistoryRow, setIpHistoryRow] = useState(null);
  const [editAssetOpen, setEditAssetOpen] = useState(false);
  const [zoneDialogOpen, setZoneDialogOpen] = useState(false);
  const [tab, setTab] = useState("identity");
  const [orionVisible, setOrionVisible] = useState(true);
  const [otVisible, setOtVisible] = useState(true);
  const [exposureByIp, setExposureByIp] = useState({});

  // `orion: orionVisible` — senza questo il backend (getRecentHostV2) tratta
  // la richiesta come Orion-disabled e strippa classification/os/risk/cves
  // dalla risposta a prescindere dal reale stato del toggle, lasciando la
  // tab Identity vuota su quei campi.
  const loadRows = () => API.getRecentHost({ serials: JSON.stringify([]), all: true, orion: orionVisible }).then((res) => {
    const data = res.data || res;
    if (data?.success) setRows(mapAssetRows(data.data));
  });

  const loadZones = () => API.getZones().then((res) => { if (res.data?.success) setZones(res.data.data || []); });

  const loadAcks = () => API.getCveAcks().then((res) => { if (res.data?.success) setAcks(res.data.data || []); });

  useEffect(() => {
    setLoading(true);
    Promise.all([loadRows(), loadZones(), loadAcks()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orionVisible]);

  // Connection count + external-IP reachability, one bulk aggregate for the
  // tenant — used only to show how exposed this asset is next to its CVE list
  // (same tie-breaker the Network Overview's "Security" tab and the dashboard's
  // Orion widget use). Independent of the main fetch above.
  useEffect(() => {
    API.getAssetExposure()
      .then((res) => setExposureByIp(res?.data?.success ? res.data.data || {} : {}))
      .catch((err) => console.error("[AssetDetailPage] failed to fetch asset exposure:", err));
  }, []);

  // Orion-gated visibility: hides the Security/Connections tabs and the summary
  // cards when the platform reports Orion as disabled for this deployment.
  useEffect(() => {
    API.getOrionVisibility()
      .then((res) => {
        const visible = res?.data?.visibility !== false;
        setOrionVisible(visible);
        if (!visible) {
          setTab((current) => (current === "security" || current === "traffic" || current === "mib" ? "identity" : current));
        }
      })
      .catch(() => {});
  }, []);

  // OT/Antlia-gated visibility: hides the OT Operations tab when this deployment's
  // licence doesn't include the "ot" module — same `modules[].enabled` check
  // lecsManagement's DeviceSettings.jsx already uses to gate Antlia Settings.
  useEffect(() => {
    API.getLicense()
      .then((res) => {
        const modules = res.data?.modules ?? [];
        const visible = modules.find((m) => m.name === "ot")?.enabled ?? false;
        setOtVisible(visible);
        if (!visible) {
          setTab((current) => (current === "ot-operations" ? "identity" : current));
        }
      })
      .catch(() => setOtVisible(false));
  }, []);

  const row = useMemo(() => rows.find((r) => r.id === assetId), [rows, assetId]);
  const zoneById = useMemo(() => Object.fromEntries(zones.map((z) => [z._id ?? z.id, z])), [zones]);
  // Fallback for an asset the probe placed in a zone (by name/subnet match)
  // that was never formally assigned via zone_id — if a dashboard zone with
  // that same name exists, its risk still applies. Without this, any asset
  // relying only on the probe's own zone guess showed the zone name/Purdue
  // level with no color/risk at all, even when an admin had set a risk level
  // for a zone of that exact name.
  const zoneByName = useMemo(
    () => Object.fromEntries(zones.map((z) => [String(z.name ?? "").trim().toLowerCase(), z])),
    [zones]
  );
  const ackMap = useMemo(
    () => new Map(acks.map((a) => [vulnAckKey(a.asset_id, a.cve_id, a.origin), a])),
    [acks]
  );

  const handleAcknowledgeCve = (aId, cveId, originKey, note) =>
    API.acknowledgeCve({ asset_id: aId, cve_id: cveId, origin: originKey, note }).then(loadAcks);

  const handleReopenCve = (aId, cveId, originKey) =>
    API.unacknowledgeCve({ asset_id: aId, cve_id: cveId, origin: originKey }).then(loadAcks);

  const handleSaveAssetFields = (fields) => {
    startToast();
    return API.updateAssetFields(row.id, fields)
      .then((res) => {
        const rejected = res.data?.rejectedFields ?? [];
        updateToastSuccess(
          rejected.length > 0
            ? `Saved, but ${rejected.length} field(s) could not be updated: ${rejected.join(", ")}`
            : "Asset updated successfully"
        );
        return loadRows();
      })
      .catch((err) => {
        updateToastError(err.response?.data?.error || "Failed to save asset changes");
        throw err;
      });
  };

  // Notes edit inline on the Identity tab now (NotesCard in AssetDetailContent)
  // instead of through the Edit Asset dialog — same endpoint/whitelist, just a
  // different entry point.
  const handleSaveNotes = (text) => handleSaveAssetFields({ notes: text });

  const handleAssignZone = (zoneId) => {
    if (!zoneId || !row) return;
    API.assignZone(zoneId, [row.id])
      .then(() => { loadRows(); setZoneDialogOpen(false); })
      .catch((err) => console.error("[handleAssignZone] Error:", err));
  };

  const handleUnassignZone = () => {
    if (!row) return;
    API.assignZone(null, [row.id])
      .then(() => { loadRows(); setZoneDialogOpen(false); })
      .catch((err) => console.error("[handleUnassignZone] Error:", err));
  };

  const handleCreateZone = (payload) => {
    API.createZone(payload).then((res) => { if (res.data.success) loadZones(); })
      .catch((err) => console.error("[handleCreateZone] Error:", err));
  };

  const handleUpdateZone = (id, payload) => {
    API.updateZone(id, payload).then((res) => { if (res.data.success) loadZones(); })
      .catch((err) => console.error("[handleUpdateZone] Error:", err));
  };

  const handleDeleteZone = (id) => {
    API.deleteZone(id).then((res) => { if (res.data.success) { loadZones(); loadRows(); } })
      .catch((err) => console.error("[handleDeleteZone] Error:", err));
  };

  // Identity falls back hostname → IP → MAC. The document's `asset_key` is an
  // internal primary key and is deliberately never shown, here or anywhere else.
  const title = row ? (assetIdentity(row) || "Asset") : "";
  const subtitle = row && row.ip && title !== row.ip ? row.ip : row?.mac || "";
  const klass = row ? resolveClassification(row.classification) : null;
  const DeviceIcon = klass?.icon;
  // Same primary-score rule as the Security tab's Risk Score card: the dynamic
  // score (live telemetry, superset of the static signals) leads whenever the
  // asset has one; documents predating dynamic_risk_score fall back to the
  // static risk_score so nothing regresses.
  const primaryRiskScore = typeof row?.dynamic_risk_score === "number" ? row.dynamic_risk_score : row?.risk_score;
  const risk = row && riskScoreStyle(primaryRiskScore);
  // Assigned zone (the dashboard's own grouping) leads over the probe's own
  // zone/Purdue guess — it carries a human risk assessment, the probe's doesn't.
  // Falls back to the probe's zone name only when nothing has been assigned.
  const probeZone = row?.zone;
  const assignedZone = row && (zoneById[row.zone_id] || (probeZone?.name && zoneByName[String(probeZone.name).trim().toLowerCase()]));
  const zoneName = assignedZone?.name || probeZone?.name || null;
  const zonePurdue = assignedZone?.purdue_level ?? probeZone?.purdue_level ?? probeZone?.purdue_level_auto?.value ?? null;
  const zoneStyle = assignedZone ? zoneRiskStyle(assignedZone.risk) : null;
  // Assigned zones carry a manually-assessed risk (zoneUtils' own scale, "none"
  // through "critical") — the tile below used to encode it only as text color,
  // which reads as decoration rather than data. Spelling it out matches how the
  // "Risk" tile next to it shows its label, not just its color.
  const assignedZoneRiskLabel = assignedZone ? zoneRiskLabel(assignedZone.risk) : null;
  // Same formula as the network-wide score and the Network Zones/Overview pages
  // (computeZoneSecurityScore reuses computeSecurityScore, just scoped to this
  // zone's own assets) — so this number never disagrees with what those pages show.
  const zoneId = assignedZone?._id ?? assignedZone?.id ?? null;
  const zoneScore = zoneId ? computeZoneSecurityScore(zoneId, { rows, zones, acks }) : null;
  // Each tile only appears when the asset actually carries that value — CVEs is
  // the one exception, since a count of 0 is itself meaningful (checked, none
  // found) rather than absent data.
  const summary = row && orionVisible ? [
    typeof primaryRiskScore === "number" && { label: "Risk", value: risk.label, color: risk.color },
    {
      label: "Zone",
      value: zoneName
        ? [
            zoneName,
            zonePurdue != null ? `Lv ${zonePurdue}` : null,
            assignedZoneRiskLabel && assignedZoneRiskLabel !== "-" ? `${assignedZoneRiskLabel} risk` : null,
            zoneScore ? `Score ${zoneScore.score}` : null,
          ]
            .filter(Boolean)
            .join(" · ")
        : "Not assigned",
      color: zoneName ? (zoneScore?.color ?? zoneStyle?.color) : colors.grey[500],
      onClick: () => setZoneDialogOpen(true),
    },
    { label: "CVEs", value: row.vulnerabilities?.length || 0 },
    row.last_seen && { label: "Last seen", value: new Date(row.last_seen).toLocaleString() },
  ].filter(Boolean) : [];

  return (
    <Box m="20px">
      <Box display="flex" alignItems="center" gap={1.5}>
        <Tooltip title="Back">
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>
        <Header title="Asset Details" subtitle=" " />
      </Box>

      {loading ? (
        <Typography sx={{ color: colors.grey[500], p: 2 }}>Loading asset…</Typography>
      ) : !row ? (
        <Typography sx={{ color: colors.grey[500], p: 2 }}>
          Asset not found — it may have been removed, or the link is stale.
        </Typography>
      ) : (
        <Box sx={{ p: { xs: 0, md: 1 } }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3, p: { xs: 1.5, md: 2 }, borderRadius: 2, backgroundColor: "rgba(69,144,149,0.06)", border: `1px solid ${colors.grey[800]}` }}>
            {DeviceIcon && orionVisible && (
              <Tooltip title={klass.path || "Not classified"}>
                <Box sx={{ width: 46, height: 46, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}` }}>
                  <DeviceIcon style={{ fontSize: 26, color: colors.grey[100] }} />
                </Box>
              </Tooltip>
            )}
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: colors.grey[100] }}>{title}</Typography>
              {subtitle && (
                <Typography sx={{ fontSize: 12.5, color: colors.grey[400] }}>{subtitle}</Typography>
              )}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6, mt: 1 }}>
                {row.vendor && <Chip label={row.vendor} size="small" sx={{ height: 19, bgcolor: "#ffffff0b", color: colors.grey[400], fontSize: 10 }} />}
              </Box>
            </Box>
            <Box sx={{ ml: "auto" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {orionVisible && (
                  <Tooltip title="Edit asset">
                    <IconButton onClick={() => setEditAssetOpen(true)} sx={{ color: colors.grey[300], p: 0.6 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <StatusBadge status={row.status} />
              </Box>
            </Box>
          </Box>

          {summary.length > 0 && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1, mb: 3 }}>
              {summary.map((item) => (
                <Box
                  key={item.label}
                  onClick={item.onClick}
                  sx={{
                    minWidth: 0, px: 1.5, py: 1.2, borderRadius: 1.5, backgroundColor: "#1c1c1c", border: `1px solid ${colors.grey[800]}`,
                    cursor: item.onClick ? "pointer" : "default",
                    ...(item.onClick && { "&:hover": { borderColor: "#459095" } }),
                  }}
                >
                  <Typography sx={{ fontSize: 10, color: colors.grey[500], textTransform: "uppercase", letterSpacing: 0.5, mb: 0.4 }}>{item.label}</Typography>
                  <Typography noWrap sx={{ fontSize: 12, fontWeight: 700, color: item.color || colors.grey[100], overflow: "hidden", textOverflow: "ellipsis" }}>{item.value}</Typography>
                </Box>
              ))}
            </Box>
          )}

          <TabContext value={tab}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <TabList
                onChange={(_, v) => setTab(v)}
                sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: 13 } }}
              >
                <Tab label="Identity" value="identity" />
                {/* TabList clones its DIRECT children to inject selected/onChange/value --
                    wrapping Tab in Tooltip+span (needed to show a tooltip on a disabled
                    button) breaks that injection, so the tab never gets marked selected
                    and never notifies TabList of a click. Only apply the wrapper when the
                    tab is actually disabled; when enabled, render Tab as a direct child. */}
                {orionVisible
                  ? <Tab label="Security" value="security" />
                  : (
                    <Tooltip title="Orion module is disabled">
                      <span><Tab label="Security" value="security" disabled /></span>
                    </Tooltip>
                  )}
                {orionVisible
                  ? <Tab label="Connections" value="traffic" />
                  : (
                    <Tooltip title="Orion module is disabled">
                      <span><Tab label="Connections" value="traffic" disabled /></span>
                    </Tooltip>
                  )}
                {otVisible
                  ? <Tab label="Operations" value="ot-operations" />
                  : (
                    <Tooltip title="OT module is disabled">
                      <span><Tab label="Operations" value="ot-operations" disabled /></span>
                    </Tooltip>
                  )}
                <Tab label="Traffic Logs" value="traffic-logs" />
                <Tab label="Network Issues" value="network-issues" />
                {orionVisible
                  ? <Tab label="MIB" value="mib" />
                  : (
                    <Tooltip title="Orion module is disabled">
                      <span><Tab label="MIB" value="mib" disabled /></span>
                    </Tooltip>
                  )}
              </TabList>
            </Box>

            {/* Identity and Security are separate tabs, not two halves of one page:
                "what is this device" and "how worried should I be" are different
                questions, and answering both at once answers neither. */}
            <TabPanel value="identity" sx={{ p: 0 }}>
              <AssetDetailContent
                section="identity"
                layout="columns"
                row={row}
                onShowIpHistory={setIpHistoryRow}
                assetId={row.id}
                ackMap={ackMap}
                onAcknowledge={handleAcknowledgeCve}
                onReopen={handleReopenCve}
                onSaveNotes={handleSaveNotes}
                orionVisible={orionVisible}
              />
            </TabPanel>

            {orionVisible && (
              <TabPanel value="security" sx={{ p: 0 }}>
                <AssetDetailContent
                  section="security"
                  layout="columns"
                  row={row}
                  onShowIpHistory={setIpHistoryRow}
                  assetId={row.id}
                  ackMap={ackMap}
                  onAcknowledge={handleAcknowledgeCve}
                  onReopen={handleReopenCve}
                  exposureByIp={exposureByIp}
                />
              </TabPanel>
            )}

            {orionVisible && (
              <TabPanel value="traffic" sx={{ p: 0 }}>
                <TrafficConnectionsPanel ip={row.ip} />
              </TabPanel>
            )}

            {otVisible && (
              <TabPanel value="ot-operations" sx={{ p: 0 }}>
                <AssetDetailContent
                  section="ot-operations"
                  layout="single"
                  row={row}
                />
              </TabPanel>
            )}

            <TabPanel value="traffic-logs" sx={{ p: 0 }}>
              <TrafficLogsPanel ip={row.ip} multiSecurityView={multiSecurityView} />
            </TabPanel>

            <TabPanel value="network-issues" sx={{ p: 0 }}>
              <NetworkIssuesPanel ip={row.ip} />
            </TabPanel>

            {orionVisible && (
              <TabPanel value="mib" sx={{ p: 0 }}>
                <MibPanel assetId={row.id} snmpLastScan={row.snmp_last_scan} />
              </TabPanel>
            )}
          </TabContext>
        </Box>
      )}

      <IpHistoryDialog row={ipHistoryRow} onClose={() => setIpHistoryRow(null)} />
      <EditAssetDialog row={row} open={editAssetOpen} onClose={() => setEditAssetOpen(false)} onSave={handleSaveAssetFields} onOverrideSaved={loadRows} />
      <ZoneManagerDialog
        open={zoneDialogOpen}
        onClose={() => setZoneDialogOpen(false)}
        zones={zones}
        selectedCount={row ? 1 : 0}
        onAssign={handleAssignZone}
        onUnassign={handleUnassignZone}
        onCreateZone={handleCreateZone}
        onUpdateZone={handleUpdateZone}
        onDeleteZone={handleDeleteZone}
      />
    </Box>
  );
};

export default AssetDetailPage;